// helpers/constants
import * as G from '../../helpers';
import { ENUMS } from '../../constants/enums';
// feature driver-cards-v2
import {
  RESOURCE_GROUP_KIND,
  RESOURCE_MODE_TRUCK,
} from './constants';
//////////////////////////////////////////////////

export const GROUP_SCHEDULE_ROW = 'GROUP_SCHEDULE_ROW';
export const RESOURCE_SCHEDULE_ROW = 'RESOURCE_SCHEDULE_ROW';

const EMPTY_GUID_TOKEN = '__none__';

const getGuidToken = (guid) => (G.isNilOrEmpty(guid) ? EMPTY_GUID_TOKEN : guid);

const getGroupKey = (groupGuid) => `group:${getGuidToken(groupGuid)}`;
const getDispatcherKey = (dispatcherGuid) => `dispatcher:${getGuidToken(dispatcherGuid)}`;

const getNestedDispatcherKey = (groupGuid, dispatcherGuid) => (
  `${getGroupKey(groupGuid)}:${getDispatcherKey(dispatcherGuid)}`
);

const isNestedGrouping = (resourceGroupBy) => (
  resourceGroupBy === ENUMS.ENUM_GROUP_AND_DISPATCHER
);

const getResourceParentGroupKey = (resource) => getGroupKey(resource.groupGuid);

export const getResourceGroupKey = (resource, resourceGroupBy) => {
  if (resourceGroupBy === ENUMS.ENUM_DISPATCHER) {
    return getDispatcherKey(resource.dispatcherGuid);
  }

  if (isNestedGrouping(resourceGroupBy)) {
    return getNestedDispatcherKey(resource.groupGuid, resource.dispatcherGuid);
  }

  return getGroupKey(resource.groupGuid);
};

export const createResourceGroupingState = () => ({
  loadedKeys: [],
  errorsByKey: {},
  loadingKeys: [],
  expandedKeys: [],
  resourceIdsByKey: {},
});

export const createLoadedResourceGrouping = (resources, resourceGroupBy) => {
  const resourceIdsByKey = resources.reduce((result, resource) => {
    const key = getResourceGroupKey(resource, resourceGroupBy);
    const ids = result[key] || [];

    return { ...result, [key]: [...ids, resource.id] };
  }, {});

  const loadedKeys = Object.keys(resourceIdsByKey);

  const expandedKeys = isNestedGrouping(resourceGroupBy)
    ? [
      ...new Set([
        ...loadedKeys,
        ...resources.map(getResourceParentGroupKey),
      ]),
    ]
    : loadedKeys;

  return {
    ...createResourceGroupingState(),
    loadedKeys,
    expandedKeys,
    resourceIdsByKey,
  };
};

const getGroupLabel = (group) => (
  group && (group.name || group.groupName || group.fullText)
);

const getDispatcherLabel = (dispatcher) => {
  if (!dispatcher) return null;

  const { firstLastMiddleNames, fullText, loginId } = G.getUserInfo(dispatcher);

  return firstLastMiddleNames
    || fullText
    || loginId
    || dispatcher.name
    || dispatcher.dispatcherName;
};

const getGroupDefinition = (group, fallbackGuid) => {
  const guid = G.getGuidFromObject(group) || fallbackGuid || null;

  return {
    guid,
    key: getGroupKey(guid),
    kind: RESOURCE_GROUP_KIND,
    label: getGroupLabel(group) || (
      guid
        ? G.getWindowLocale('titles:dispatch-group', 'Dispatch Group')
        : G.getWindowLocale('titles:no-group', 'No Group')
    ),
  };
};

const getDispatcherDefinition = (
  dispatcher,
  fallbackGuid,
  groupGuid = null,
  nested = false,
) => {
  const guid = G.getGuidFromObject(dispatcher) || fallbackGuid || null;

  const key = nested
    ? getNestedDispatcherKey(groupGuid, guid)
    : getDispatcherKey(guid);

  return {
    key,
    guid,
    groupGuid,
    kind: ENUMS.ENUM_DISPATCHER,
    label: getDispatcherLabel(dispatcher) || (
      guid
        ? G.getWindowLocale('titles:dispatcher', 'Dispatcher')
        : G.getWindowLocale('titles:no-dispatcher', 'No Dispatcher')
    ),
  };
};

const addDefinition = (definitions, definition) => {
  if (!definitions.has(definition.key)) definitions.set(definition.key, definition);
};

const getResourceGroupEntity = (resource) => {
  const source = resource.source || {};

  return source.dispatchingGroup || source.group || {
    guid: resource.groupGuid,
    name: source.dispatchingGroupName || source.groupName,
  };
};

const getResourceDispatcherEntity = (resource) => {
  const source = resource.source || {};

  return source.assignedDispatcher || source.dispatcher || {
    guid: resource.dispatcherGuid,
    name: source.assignedDispatcherName || source.dispatcherName,
  };
};

const getResourceRows = (resourceIds, resourcesById, groupKey) => (
  resourceIds.reduce((rows, resourceId) => {
    const resource = resourcesById.get(resourceId);

    if (!resource) return rows;

    return [
      ...rows,
      {
        resource,
        kind: RESOURCE_SCHEDULE_ROW,
        id: `resource-row:${groupKey}:${resourceId}`,
      },
    ];
  }, [])
);

const getHeaderRow = ({
  level,
  grouping,
  count = 0,
  definition,
  loadResources = true,
}) => ({
  count,
  level,
  loadResources,
  label: definition.label,
  kind: GROUP_SCHEDULE_ROW,
  groupKey: definition.key,
  groupKind: definition.kind,
  id: `group-row:${definition.key}`,
  error: grouping.errorsByKey[definition.key],
  loaded: grouping.loadedKeys.includes(definition.key),
  loading: grouping.loadingKeys.includes(definition.key),
  expanded: grouping.expandedKeys.includes(definition.key),
  dispatcherGuid: definition.kind === ENUMS.ENUM_DISPATCHER ? definition.guid : null,
  groupGuid: definition.kind === RESOURCE_GROUP_KIND ? definition.guid : definition.groupGuid,
});

const buildSingleLevelRows = ({
  grouping,
  groupInfo,
  resources,
  resourceGroupBy,
}) => {
  const dispatcherGrouping = resourceGroupBy === ENUMS.ENUM_DISPATCHER;

  const metadata = dispatcherGrouping
    ? (groupInfo.dispatchers || [])
    : (groupInfo.dispatchingGroups || []);

  const definitions = new Map();
  const resourcesById = new Map(resources.map((resource) => [resource.id, resource]));

  metadata.forEach((entity) => addDefinition(
    definitions,
    dispatcherGrouping ? getDispatcherDefinition(entity) : getGroupDefinition(entity),
  ));
  resources.forEach((resource) => addDefinition(
    definitions,
    dispatcherGrouping
      ? getDispatcherDefinition(getResourceDispatcherEntity(resource), resource.dispatcherGuid)
      : getGroupDefinition(getResourceGroupEntity(resource), resource.groupGuid),
  ));

  return [...definitions.values()].flatMap((definition) => {
    const resourceIds = grouping.resourceIdsByKey[definition.key] || [];

    const header = getHeaderRow({
      grouping,
      level: 0,
      definition,
      count: resourceIds.length,
    });

    return header.expanded
      ? [header, ...getResourceRows(resourceIds, resourcesById, definition.key)]
      : [header];
  });
};

const buildNestedRows = ({ groupInfo, resources, grouping }) => {
  const groupDefinitions = new Map();
  const dispatcherDefinitionsByGroup = new Map();
  const resourcesById = new Map(resources.map((resource) => [resource.id, resource]));

  const addDispatcher = (groupGuid, dispatcher, fallbackGuid) => {
    const parentKey = getGroupKey(groupGuid);
    const definitions = dispatcherDefinitionsByGroup.get(parentKey) || new Map();
    const definition = getDispatcherDefinition(dispatcher, fallbackGuid, groupGuid, true);

    addDefinition(definitions, definition);
    dispatcherDefinitionsByGroup.set(parentKey, definitions);
  };

  (groupInfo.dispatchingGroups || []).forEach((group) => {
    const definition = getGroupDefinition(group);

    addDefinition(groupDefinitions, definition);
    (group && group.dispatchers ? group.dispatchers : []).forEach((dispatcher) => (
      addDispatcher(definition.guid, dispatcher)
    ));
  });
  resources.forEach((resource) => {
    addDefinition(
      groupDefinitions,
      getGroupDefinition(getResourceGroupEntity(resource), resource.groupGuid),
    );
    addDispatcher(
      resource.groupGuid,
      getResourceDispatcherEntity(resource),
      resource.dispatcherGuid,
    );
  });

  return [...groupDefinitions.values()].flatMap((groupDefinition) => {
    const dispatcherDefinitions = [
      ...(dispatcherDefinitionsByGroup.get(groupDefinition.key) || new Map()).values(),
    ];

    const groupResourceCount = dispatcherDefinitions.reduce((count, definition) => (
      count + (grouping.resourceIdsByKey[definition.key] || []).length
    ), 0);

    const groupHeader = getHeaderRow({
      grouping,
      level: 0,
      loadResources: false,
      count: groupResourceCount,
      definition: groupDefinition,
    });

    if (!groupHeader.expanded) return [groupHeader];

    const childRows = dispatcherDefinitions.flatMap((definition) => {
      const resourceIds = grouping.resourceIdsByKey[definition.key] || [];

      const dispatcherHeader = getHeaderRow({
        grouping,
        level: 1,
        definition,
        count: resourceIds.length,
      });

      return dispatcherHeader.expanded
        ? [
          dispatcherHeader,
          ...getResourceRows(resourceIds, resourcesById, definition.key),
        ]
        : [dispatcherHeader];
    });

    return [groupHeader, ...childRows];
  });
};

export const buildScheduleRows = ({
  grouping,
  groupInfo,
  resources,
  resourceMode,
  resourceGroupBy,
}) => {
  if (resourceGroupBy === ENUMS.ENUM_NONE) {
    return resources.map((resource) => ({
      resource,
      kind: RESOURCE_SCHEDULE_ROW,
      id: `resource-row:${resource.id}`,
    }));
  }

  const info = groupInfo || {};

  if (
    resourceMode === RESOURCE_MODE_TRUCK
    && isNestedGrouping(resourceGroupBy)
  ) {
    return buildNestedRows({ groupInfo: info, resources, grouping });
  }

  return buildSingleLevelRows({
    grouping,
    resources,
    groupInfo: info,
    resourceGroupBy,
  });
};
